import { defineStore } from 'pinia'
import { ref, watch } from 'vue'
import { useIntervalFn, useDocumentVisibility } from '@vueuse/core'
import { notificationsService } from '@/services/NotificationsService'
import { companiesService } from '@/services/CompaniesService'

const POLL_INTERVAL_MS = 60000

export const useNotificationsStore = defineStore('notifications', () => {
  const notifications = ref([])
  const unreadCount = ref(0)
  const isLoading = ref(false)
  const isLoadingMore = ref(false)
  const error = ref(null)
  const currentPage = ref(1)
  const hasNextPage = ref(false)

  async function fetchNotifications({ page = 1, limit = 20, unreadOnly = false } = {}) {
    if (!companiesService.hasActiveCompany()) {
      notifications.value = []
      unreadCount.value = 0
      return
    }
    isLoading.value = true
    error.value = null
    try {
      const response = await notificationsService.getAll({ page, limit, unreadOnly })
      const payload = response?.data ?? response
      notifications.value = Array.isArray(payload?.notifications) ? payload.notifications : []
      // unread_count del backend es SIEMPRE el total real del destinatario; nunca se recalcula acá
      unreadCount.value = Number(payload?.unread_count) || 0
      currentPage.value = payload?.pagination?.page || 1
      hasNextPage.value = !!payload?.pagination?.hasNextPage
    } catch (err) {
      error.value = err?.message || 'Error al cargar notificaciones'
    } finally {
      isLoading.value = false
    }
  }

  // Scroll infinito: agrega la siguiente página al final en vez de reemplazar la lista
  async function loadMoreNotifications({ limit = 20, unreadOnly = false } = {}) {
    if (isLoadingMore.value || isLoading.value || !hasNextPage.value) return
    isLoadingMore.value = true
    try {
      const response = await notificationsService.getAll({ page: currentPage.value + 1, limit, unreadOnly })
      const payload = response?.data ?? response
      const nextRows = Array.isArray(payload?.notifications) ? payload.notifications : []
      notifications.value = [...notifications.value, ...nextRows]
      currentPage.value = payload?.pagination?.page || currentPage.value + 1
      hasNextPage.value = !!payload?.pagination?.hasNextPage
    } catch (err) {
      error.value = err?.message || 'Error al cargar más notificaciones'
    } finally {
      isLoadingMore.value = false
    }
  }

  async function markRead(id) {
    const target = notifications.value.find((n) => n.id === id)
    const wasUnread = !!target && !target.read_at
    try {
      const response = await notificationsService.markRead(id)
      const updated = response?.data ?? response
      if (target) target.read_at = updated?.read_at ?? new Date().toISOString()
      if (wasUnread) unreadCount.value = Math.max(0, unreadCount.value - 1)
    } catch (err) {
      error.value = err?.message || 'Error al marcar la notificación como leída'
      throw err
    }
  }

  async function markAllRead() {
    try {
      await notificationsService.markAllRead()
      const now = new Date().toISOString()
      notifications.value = notifications.value.map((n) => ({ ...n, read_at: n.read_at ?? now }))
      unreadCount.value = 0
    } catch (err) {
      error.value = err?.message || 'Error al marcar todas las notificaciones como leídas'
      throw err
    }
  }

  // D4: el intervalo vive en el store (sobrevive a remounts del header), pero su ciclo
  // de vida (start/stop) lo dispara App.vue como único componente siempre montado.
  const { pause: pauseInterval, resume: resumeInterval } = useIntervalFn(
    fetchNotifications,
    POLL_INTERVAL_MS,
    { immediate: false }
  )
  const documentVisibility = useDocumentVisibility()
  // Refleja si App.vue pidió polling activo; sin este flag, un cambio de visibilidad
  // podría reanudar el intervalo aunque el usuario ya haya cerrado sesión.
  const pollingEnabled = ref(false)

  watch(documentVisibility, (visibility) => {
    if (!pollingEnabled.value) return
    if (visibility === 'hidden') {
      pauseInterval()
    } else {
      resumeInterval()
      fetchNotifications()
    }
  })

  function startPolling() {
    pollingEnabled.value = true
    if (documentVisibility.value === 'hidden') return
    fetchNotifications()
    resumeInterval()
  }

  function stopPolling() {
    pollingEnabled.value = false
    pauseInterval()
  }

  // Composition stores no tienen $reset nativo (es solo de Options API) — reset explícito,
  // requerido por convención del proyecto en cada punto de cambio de empresa activa.
  function reset() {
    notifications.value = []
    unreadCount.value = 0
    isLoading.value = false
    isLoadingMore.value = false
    error.value = null
    currentPage.value = 1
    hasNextPage.value = false
  }

  return {
    notifications,
    unreadCount,
    isLoading,
    isLoadingMore,
    hasNextPage,
    error,
    fetchNotifications,
    loadMoreNotifications,
    markRead,
    markAllRead,
    startPolling,
    stopPolling,
    reset
  }
})
